import { Component, inject, OnInit } from '@angular/core';
import { OrganizationType } from '../../../shared/enums/organization-type.enum';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, map, Observable, switchMap } from 'rxjs';
import { DefaultAccessGroup } from '../access-group.model';
import {
  AccessGroupService,
  CreateDefaultAccessGroupRequest,
  UpdateDefaultAccessGroupRequest,
} from '../../../shared/services/access-group.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DiscountType } from '../../../shared/enums/discount-type.enum';
import { SnackbarService } from 'ok-angular-shared/components';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { DkCurrencyPipe } from '../../../shared/pipes/dk-currency';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { EnumToArrayPipe } from '../../../shared/pipes/enum-to-array.pipe';
import { TranslateAccessGroupCarTypeEnum } from '../../../shared/pipes/translate-access-group-car-type-enum.pipe';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import {
  AccessGroupSharedDiscountSearchEvent,
  AccessGroupSharedDiscountService,
  IAccessGroupSharedDiscountSearchResultItem,
} from 'src/app/shared/services/access-group-shared-discount.service';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReactiveFormsModule } from '@angular/forms';

export type AccessGroupDefaultModalComponentData = {
  organizationType: OrganizationType;
};

@Component({
  selector: 'adm-access-group-default-modal',
  templateUrl: './access-group-default-modal.component.html',
  styleUrl: './access-group-default-modal.component.scss',
  imports: [
    CustomDropDownArrowDirective,
    DkCurrencyPipe,
    EnumToArrayPipe,
    TranslateAccessGroupCarTypeEnum,
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
})
export class AccessGroupDefaultModalComponent implements OnInit {
  data = inject<AccessGroupDefaultModalComponentData>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  accessGroupService = inject(AccessGroupService);
  private snackBarService = inject(SnackbarService);

  sharedAccessGroupDiscountService = inject(AccessGroupSharedDiscountService);

  public OrganizationType = OrganizationType;
  public defaultAccessGroupBeingEdited: DefaultAccessGroup | null;

  public updateSubject$ = new BehaviorSubject(null);
  public standardAccessGroups$: Observable<DefaultAccessGroup[]>;
  public formValid$: Observable<boolean>;

  public carTypes: typeof AccessGroupCarType = AccessGroupCarType;
  public form = new FormGroup({
    name: new FormControl<string>('', { validators: [Validators.required, Validators.minLength(1)], nonNullable: true }),
    fixedDiscountIdentifier: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    percentageDiscountIdentifier: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    carType: new FormControl<AccessGroupCarType>(AccessGroupCarType.CompanyCar, { validators: [Validators.required], nonNullable: true }),
  });
  percentageDiscounts$: Observable<IAccessGroupSharedDiscountSearchResultItem[]>;
  fixedDiscounts$: Observable<IAccessGroupSharedDiscountSearchResultItem[]>;

  ngOnInit() {
    this.percentageDiscounts$ = this.updateSubject$.pipe(
      switchMap(() =>
        this.sharedAccessGroupDiscountService.getSharedDiscounts(<AccessGroupSharedDiscountSearchEvent>{
          discountType: DiscountType.Percentages,
        })
      ),
      map(discounts => discounts.items)
    );
    this.fixedDiscounts$ = this.updateSubject$.pipe(
      switchMap(() =>
        this.sharedAccessGroupDiscountService.getSharedDiscounts(<AccessGroupSharedDiscountSearchEvent>{
          discountType: DiscountType.FixedAmount,
        })
      ),
      map(discounts => discounts.items)
    );

    this.standardAccessGroups$ = this.updateSubject$.pipe(
      switchMap(() => this.accessGroupService.getDefaultAccessGroups()),
      map(defaultAccessGroups => {
        return defaultAccessGroups.filter(dfg => dfg.type === this.data.organizationType).sort((d1, d2) => d1.name.localeCompare(d2.name));
      })
    );

    this.formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));
  }

  async deleteDefaultAccessGroupUser(defaultAccessGroup: DefaultAccessGroup) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Slet standard adgangsgruppe',
        text: `Er du sikker på at du vil slette standard adgangsgruppen ${defaultAccessGroup.name}?`,
        confirmText: 'Slet',
        denyText: 'Luk',
      },
      disableClose: true,
    });
    confirmDialogRef.afterClosed().subscribe(async confirmed => {
      if (confirmed) {
        this.accessGroupService.deleteDefaultAccessGroup(defaultAccessGroup.identifier).subscribe(() => {
          this.updateSubject$.next(null);
        });
      }
    });
  }

  editDefaultAccessGroupUser(defaultAccessGroup: DefaultAccessGroup) {
    this.defaultAccessGroupBeingEdited = defaultAccessGroup;
    this.form.setValue({
      name: defaultAccessGroup.name,
      fixedDiscountIdentifier: defaultAccessGroup.sharedDiscountFixedIdentifier,
      percentageDiscountIdentifier: defaultAccessGroup.sharedDiscountPercentageIdentifier,
      carType: defaultAccessGroup.carType,
    });
  }

  async updateDefaultAccessGroup() {
    if (!this.defaultAccessGroupBeingEdited) {
      return;
    }

    const formValue = this.form.getRawValue();
    this.accessGroupService
      .updateDefaultAccessGroup(this.defaultAccessGroupBeingEdited.identifier, <UpdateDefaultAccessGroupRequest>{
        name: formValue.name,
        shared_discount_percentage_identifier: formValue.percentageDiscountIdentifier,
        shared_discount_fixed_identifier: formValue.fixedDiscountIdentifier,
        type: this.data.organizationType,
        car_type: formValue.carType,
      })
      .subscribe(() => {
        this.form.reset();
        this.updateSubject$.next(null);
        this.defaultAccessGroupBeingEdited = null;
        this.snackBarService.success('Standard adgangsgruppen blev opdateret');
      });
  }

  async createDefaultAccessGroup() {
    const formValue = this.form.getRawValue();
    this.accessGroupService
      .createDefaultAccessGroup(<CreateDefaultAccessGroupRequest>{
        name: formValue.name,
        type: this.data.organizationType,
        shared_discount_fixed_identifier: formValue.fixedDiscountIdentifier,
        shared_discount_percentage_identifier: formValue.percentageDiscountIdentifier,
        car_type: formValue.carType,
      })
      .subscribe(() => {
        this.form.reset();
        this.updateSubject$.next(null);
        this.snackBarService.success('Standard adgangsgruppen blev oprettet');
      });
  }

  protected readonly discountTypes = DiscountType;
}
